import { ReactNode, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAdministrationAPI } from "../AdministrationAPI";

import { Invoice, InvoiceStatus } from "@the-real-insight/tri-model";
import Section from "../components/Section";
import { useModalContext } from "../components/ModalProvider";
import { useUrlPageState } from "../utilities/dataTablePagination";

import type { ColumnDef } from "@tanstack/react-table";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** Leerzustand: Überschrift, Erklärung und — wo sinnvoll — Handlungsaufforderung. */
function TableEmptyState({
    title,
    hint,
    action,
}: {
    title: string;
    hint?: string;
    action?: ReactNode;
}) {
    return (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
            {action}
        </div>
    );
}

const invoiceKey = (invoice: any) => String(invoice?.id ?? invoice?._id ?? '');

export default function InvoicingPanel(_properties: any) {
    const { t } = useTranslation();
    const fieldId = useId();
    const { pushError, pushSuccess } = useModalContext() as any;
    const { getAllInvoices, updateInvoices } = useAdministrationAPI();
    const invoicesPagination = useUrlPageState('invoicesPage');
    const currencyFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
    const [openOnlyChecked, setOpenOnlyChecked] = useState(true);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [filter, setFilter] = useState() as any;
    const [marking, setMarking] = useState(false);

    const openOnlyId = `${fieldId}-open-only`;
    const filterId = `${fieldId}-filter`;

    const filterInvoices = async () => {
        setLoading(true);
        try {
            setInvoices((await getAllInvoices(new Date('2024-05-01T00:00:00'), new Date(), openOnlyChecked, filter))
                .sort((a: Invoice, b: Invoice) => b.periodFromDate.getTime() - a.periodFromDate.getTime()));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        try {
            filterInvoices();
        } catch (error: any) {
            pushError(error);
        }
    }, [openOnlyChecked, filter]);

    const selectedInvoices: any[] = invoices.filter((invoice: any) => selectedKeys.has(invoiceKey(invoice)));

    const columns: ColumnDef<any>[] = [{
        id: 'select',
        size: 48,
        enableSorting: false,
        header: () => {
            const selectableKeys = invoices.map((invoice: any) => invoiceKey(invoice));
            const allSelected =
                selectableKeys.length > 0 && selectableKeys.every((key: string) => selectedKeys.has(key));

            return (
                <Checkbox
                    checked={allSelected}
                    aria-label={t('administrationPage.invoicingPanel.selectAllInvoices')}
                    onCheckedChange={(checked) =>
                        setSelectedKeys(checked === true ? new Set<string>(selectableKeys) : new Set<string>())
                    }
                />
            );
        },
        cell: ({ row }) => {
            const key = invoiceKey(row.original);

            return (
                <Checkbox
                    checked={selectedKeys.has(key)}
                    aria-label={`${t('global.select')}: ${row.original.id}`}
                    onCheckedChange={(checked) => {
                        const keys = new Set(selectedKeys);
                        if (checked === true) {
                            keys.add(key);
                        } else {
                            keys.delete(key);
                        }
                        setSelectedKeys(keys);
                    }}
                />
            );
        },
    }, {
        id: 'paymentStatus',
        size: 250,
        header: () => t('billingPanel.paymentStatusColumn'),
        cell: ({ row }) => (
            <div className="flex justify-center">
                <span
                    className={
                        row.original.status === InvoiceStatus.paid
                            ? 'text-sm font-bold text-success'
                            : 'text-sm font-bold text-destructive'
                    }
                >
                    {row.original.status === InvoiceStatus.paid ? t('billingPanel.paid') : t('billingPanel.open')}
                </span>
            </div>
        ),
    }, {
        id: 'invoiceId',
        size: 250,
        header: () => t('administrationPage.invoicingPanel.invoiceIDColumn'),
        cell: ({ row }) => <span className="min-w-0 break-words text-sm">{row.original.id}</span>,
    }, {
        id: 'invoiceDate',
        size: 200,
        header: () => <div className="text-right">{t('billingPanel.invoiceDateColumn')}</div>,
        cell: ({ row }) => (
            <div className="whitespace-nowrap text-right text-sm">
                {row.original.creationDate.toLocaleDateString()}
            </div>
        ),
    }, {
        id: 'invoicePeriod',
        header: () => <div className="text-right">{t('billingPanel.invoicePeriodColumn')}</div>,
        cell: ({ row }) => (
            <div className="text-right text-sm">
                {row.original.periodFromDate.toLocaleDateString()} - {new Date(row.original.periodToDate).toLocaleDateString()}
            </div>
        ),
    }, {
        id: 'amount',
        header: () => <div className="text-right">{t('billingPanel.amountColumn')}</div>,
        cell: ({ row }) => (
            <div className="whitespace-nowrap text-right text-sm tabular-nums">
                {currencyFormat.format(row.original.subtotal())}
            </div>
        ),
    }, {
        id: 'paidDate',
        header: () => t('administrationPage.invoicingPanel.paidDateColumn'),
        cell: ({ row }) => (
            <span className="whitespace-nowrap text-sm">
                {row.original.paidDate
                    ? row.original.paidDate.toLocaleDateString()
                    : t('administrationPage.invoicingPanel.notPaid')}
            </span>
        ),
    }];

    const filterActive = openOnlyChecked || Boolean(filter && String(filter).trim().length > 0);

    return <>
        <Section title={t('administrationPage.invoicingPanel.invoicesAndPayment')} className="w-full min-w-0 max-w-[1400px]">
            <div className="flex min-w-0 flex-wrap items-end gap-4">
                <div className="flex items-center gap-2 pb-2">
                    <Checkbox
                        id={openOnlyId}
                        checked={openOnlyChecked}
                        onCheckedChange={() => setOpenOnlyChecked(!openOnlyChecked)}
                    />
                    <Label htmlFor={openOnlyId}>{t('administrationPage.invoicingPanel.openOnly')}</Label>
                </div>
                <div className="flex w-[200px] min-w-0 max-w-full flex-col gap-1.5">
                    <Label htmlFor={filterId}>{t('administrationPage.invoicingPanel.filter')}</Label>
                    <Input
                        id={filterId}
                        value={filter || ''}
                        onChange={(event) => setFilter(event.target.value)}
                    />
                </div>
                <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
                    {selectedInvoices.length > 0 && (
                        <span className="text-sm text-muted-foreground">
                            {selectedInvoices.length} {t('billingPanel.selected')}
                        </span>
                    )}
                    <Button
                        className="max-sm:w-full"
                        disabled={selectedInvoices.length === 0 || marking}
                        onClick={async () => {
                            const count = selectedInvoices.length;

                            selectedInvoices.forEach((invoice: Invoice) => {
                                invoice.status = InvoiceStatus.paid;
                                invoice.paidDate = new Date();
                            });

                            setMarking(true);

                            try {
                                await updateInvoices(selectedInvoices);

                                filterInvoices();
                                setSelectedKeys(new Set());
                                pushSuccess(t('administrationPage.invoicingPanel.markedPaid', { count }));
                            } catch (error) {
                                pushError(error);
                            } finally {
                                setMarking(false);
                            }
                        }}
                    >
                        {marking && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                        {t('administrationPage.invoicingPanel.markPaid')}
                    </Button>
                </div>
            </div>
            <div className="mt-4 min-w-0 overflow-x-auto">
                <DataTable
                    columns={columns}
                    data={invoices}
                    loading={loading}
                    pageSize={10}
                    page={invoicesPagination.page}
                    onPageChange={invoicesPagination.onPageChange}
                    previousAriaLabel={t('global.back')}
                    nextAriaLabel={t('global.next')}
                    emptyMessage={
                        filterActive ? (
                            <TableEmptyState
                                title={t('administrationPage.invoicingPanel.noMatchingInvoicesTitle')}
                                hint={t('administrationPage.invoicingPanel.noMatchingInvoicesHint')}
                                action={
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setOpenOnlyChecked(false);
                                            setFilter('');
                                        }}
                                    >
                                        {t('billingPanel.resetFilter')}
                                    </Button>
                                }
                            />
                        ) : (
                            <TableEmptyState
                                title={t('administrationPage.invoicingPanel.noInvoicesTitle')}
                                hint={t('administrationPage.invoicingPanel.noInvoicesHint')}
                            />
                        )
                    }
                />
            </div>
        </Section>
    </>;
}
